/**
 * Persona na prática (seção 3.4 NOVO_MODELO_TILTI)
 * Exibe necessidades, desejos, dúvidas e objeções mapeadas a partir do histórico de conversas do bot.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import StyledSelect from './StyledSelect';

const blockStyle = {
  marginBottom: '20px',
  padding: '20px',
  background: 'rgba(10, 14, 39, 0.6)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 212, 255, 0.2)',
};
const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#B0BEC5',
  fontWeight: '600',
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
};
const listItem = { color: '#E2E8F0', marginBottom: '6px', paddingLeft: '12px', position: 'relative' };
const listItemBefore = { position: 'absolute', left: 0, color: '#00D4FF' };

const PersonaNaPratica = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    axios.get(`${API_BASE}/api/clients`)
      .then(res => setClients(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMessage({ type: 'error', text: 'Erro ao carregar clientes.' }));
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setBots([]);
      setSelectedBotId('');
      return;
    }
    setLoading(true);
    axios.get(`${API_BASE}/api/bots`, { params: { clientId: selectedClientId } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setBots(list);
        setSelectedBotId(list[0] ? list[0].id : '');
      })
      .catch(() => {
        setBots([]);
        setMessage({ type: 'error', text: 'Erro ao carregar bots.' });
      })
      .finally(() => setLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedBotId) {
      setInsights(null);
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    axios.get(`${API_BASE}/api/bots/${selectedBotId}/insights`)
      .then(res => {
        setInsights(res.data);
      })
      .catch(() => {
        setInsights(null);
        setMessage({ type: 'error', text: 'Erro ao carregar insights.' });
      })
      .finally(() => setLoading(false));
  }, [selectedBotId]);

  const handleMapPersona = async () => {
    if (!selectedBotId) return;
    setMapping(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API_BASE}/api/bots/${selectedBotId}/map-persona`, { limit: 200 });
      setInsights(res.data.insights || null);
      setMessage({
        type: 'success',
        text: `Persona mapeada com sucesso! Analisadas ${res.data.conversationCount || 0} conversas.`
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erro ao mapear persona.'
      });
    } finally {
      setMapping(false);
    }
  };

  const renderList = (items, emptyLabel) => {
    if (!items || items.length === 0) {
      return <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>{emptyLabel}</p>;
    }
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, i) => (
          <li key={i} style={listItem}><span style={listItemBefore}>•</span> {item}</li>
        ))}
      </ul>
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Persona na prática</h2>
      <p style={styles.subtitle}>
        O que seu prospect realmente mostra: necessidades, desejos, dúvidas e objeções extraídos do histórico de conversas do bot. Use para refinar o treino em &quot;Treine seu agente&quot;.
      </p>

      {message.text && (
        <div style={{
          ...styles.message,
          background: message.type === 'success' ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255, 107, 53, 0.2)',
          border: message.type === 'success' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255, 107, 53, 0.5)',
          color: message.type === 'success' ? '#00D4FF' : '#FF6B35'
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div style={{ minWidth: '200px' }}>
          <label style={labelStyle}>Cliente</label>
          <StyledSelect
            value={selectedClientId}
            onChange={setSelectedClientId}
            options={[
              { value: '', label: '— Selecione o cliente —' },
              ...clients.map(c => ({ value: c.id || c._id, label: c.name }))
            ]}
            placeholder="— Selecione o cliente —"
          />
        </div>
        <div style={{ minWidth: '260px' }}>
          <label style={labelStyle}>Bot (agente)</label>
          <StyledSelect
            value={selectedBotId}
            onChange={setSelectedBotId}
            options={[
              { value: '', label: '— Selecione o bot —' },
              ...bots.map(b => ({ value: b.id, label: `${b.name} (${b.id})` }))
            ]}
            placeholder="— Selecione o bot —"
            disabled={loading || !bots.length}
          />
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            onClick={handleMapPersona}
            disabled={!selectedBotId || mapping}
            style={{
              ...styles.btn,
              opacity: (!selectedBotId || mapping) ? 0.6 : 1,
              cursor: (!selectedBotId || mapping) ? 'not-allowed' : 'pointer'
            }}
          >
            {mapping ? 'Mapeando...' : 'Mapear persona agora'}
          </button>
        </div>
      </div>

      {loading && selectedBotId && !insights && <p style={styles.loading}>Carregando...</p>}

      {!loading && selectedBotId && insights === null && (
        <div style={blockStyle}>
          <p style={{ color: '#B0BEC5', margin: 0 }}>
            Nenhum mapeamento ainda para este bot. Clique em <strong>Mapear persona agora</strong> para analisar as conversas e extrair necessidades, desejos, dúvidas e objeções (usa IA; requer conversas no histórico permanente).
          </p>
        </div>
      )}

      {!loading && insights && (
        <>
          <div style={{ ...blockStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ color: '#B0BEC5', fontSize: '13px' }}>
              Último mapeamento: {insights.mappedAt ? new Date(insights.mappedAt).toLocaleString('pt-BR') : '—'} · {insights.conversationCount || 0} conversas analisadas
            </span>
          </div>

          <div style={blockStyle}>
            <h3 style={styles.sectionTitle}>Necessidades (o que as pessoas estão buscando)</h3>
            {renderList(insights.necessidades, 'Nenhuma necessidade mapeada.')}
          </div>
          <div style={blockStyle}>
            <h3 style={styles.sectionTitle}>Desejos (o que querem alcançar ou obter)</h3>
            {renderList(insights.desejos, 'Nenhum desejo mapeado.')}
          </div>
          <div style={blockStyle}>
            <h3 style={styles.sectionTitle}>Dúvidas frequentes</h3>
            {renderList(insights.duvidas, 'Nenhuma dúvida mapeada.')}
          </div>
          <div style={blockStyle}>
            <h3 style={styles.sectionTitle}>Objeções recorrentes (resistências, medos)</h3>
            {renderList(insights.objeções, 'Nenhuma objeção mapeada.')}
          </div>
        </>
      )}

      {/* Vídeo de ajuda - Persona na prática */}
      <section style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0, 212, 255, 0.2)' }}>
        <h3 style={{ color: '#00D4FF', fontSize: '1rem', fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Vídeo de ajuda</h3>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: 800, margin: '0 auto', borderRadius: 12, border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <iframe
            title="Persona na prática"
            src="https://www.loom.com/embed/182ae4d133c842b287d47020270a6103"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            frameBorder="0"
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  title: {
    fontSize: '1.8rem',
    marginBottom: '8px',
    color: '#FFFFFF',
    fontWeight: '700',
  },
  subtitle: {
    color: '#B0BEC5',
    marginBottom: '24px',
    fontSize: '14px',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  loading: {
    color: '#B0BEC5',
    fontStyle: 'italic',
  },
  btn: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid rgba(0, 212, 255, 0.4)',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #B026FF 0%, #0066FF 100%)',
    color: '#fff',
  },
  sectionTitle: {
    fontSize: '1rem',
    color: '#00D4FF',
    marginTop: 0,
    marginBottom: '12px',
    fontWeight: '600',
  },
};

export default PersonaNaPratica;
